import { CloseOutline18Icon } from "@vygruppen/spor-icon-react";
import { CSSProperties, FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { AlertsCounter } from "shared/components/dataDisplay/AlertsCounter";
import { Button } from "shared/components/Spor";
import styled, { css, useTheme } from "styled-components";

type SectionButtonVariant = "primary" | "secondary" | "tertiary" | "ghost";

const sectionButtonStyle = (checked = false, emphasizeText = false) => css`
  ${emphasizeText
    ? css`
        color: ${({ theme }) =>
          checked ? theme.colorTextMain : theme.colorSelected};
        font-weight: ${checked ? "normal" : "bold"};
      `
    : ""}
  outline: 1px solid
    ${({ theme }) =>
    checked ? theme.colorOutlineFocus : theme.colorOutlineMain};
  outline-offset: 0;

  &:hover {
    outline: 2px solid
      ${({ theme }) =>
        checked ? theme.colorOutlineFocus : theme.colorTextMain};
  }
  &:active {
    border: 1px solid ${({ theme }) => theme.colorOutlineFocus};
  }
  &:focus {
    border: 1px solid
      ${({ theme }) =>
        checked ? theme.colorOutlineFocus : theme.colorOutlineMain};
    outline: none;
  }
  &:focus:active {
    filter: ${({ theme }) => theme.filterSelected};
    border: 1px solid
      ${({ theme }) =>
        checked ? theme.colorOutlineFocus : theme.colorOutlineMain};
    outline: none;
  }
`;

const getSectionButtonStyle = (
  variant: SectionButtonVariant,
  checked = false,
) => {
  switch (variant) {
    case "primary":
      return sectionButtonStyle(checked, true);
    case "secondary":
    case "tertiary":
      return sectionButtonStyle(checked);
    case "ghost":
      return checked ? sectionButtonStyle(checked) : css``;
  }
};

type SectionButtonProps = {
  checked: boolean;
  onCheckedChange: () => void;
  alerts?: number;
  icon?: ReactElement | null;
  variant?: SectionButtonVariant;
  label?: string;
  style?: CSSProperties;
  "data-testid"?: string;
  height?: string;
  fontSize?: string;
};

const StyledSectionButton = styled(Button)<{
  $checked: boolean;
  $sectionVariant: SectionButtonVariant;
}>`
  ${({ $checked, $sectionVariant }) =>
    getSectionButtonStyle($sectionVariant, $checked)}
`;

export const SectionButton: FC<SectionButtonProps> = ({
  checked,
  onCheckedChange,
  alerts = 0,
  icon = null,
  variant = "tertiary",
  label,
  style,
  height,
  fontSize,
  "data-testid": dataTestId,
}: SectionButtonProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <StyledSectionButton
      $checked={checked}
      $sectionVariant={variant}
      size="sm"
      variant={variant}
      leftIcon={icon ?? undefined}
      onClick={onCheckedChange}
      backgroundColor={checked ? theme.colorSelected : undefined}
      height={height}
      fontSize={fontSize}
      style={style}
      data-testid={dataTestId}
    >
      {label ?? t("Filter")}
      {alerts ? (
        <AlertsCounter semantics="transparent" alerts={alerts} />
      ) : null}
      {checked ? <CloseOutline18Icon mt="1px" /> : null}
    </StyledSectionButton>
  );
};
